import type { BudgetResponse } from '@/api/budget'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { perDay } from '@/lib/budgetPace'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { cn } from '@/lib/utils'

export function BudgetHero({
  data,
  periodLabel,
  progress,
}: {
  data: BudgetResponse
  periodLabel: string
  progress: { isCurrent: boolean; elapsedFraction: number; daysLeft: number }
}) {
  const { formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const { spending, savings } = data
  // Optional: the web app can be live before the API that adds them.
  const counted = spending.countedExpenses ?? spending.totalExpenses
  const yearlySpent = spending.yearlyBudgetedExpenses ?? 0
  const limit = spending.budgetLimit
  const left = limit != null ? fromPaise(toPaise(limit) - toPaise(counted)) : null
  const isOver = left != null && left < 0
  const daily = progress.isCurrent && left != null ? perDay(left, progress.daysLeft) : null

  const leadLabel = left == null ? 'Spent' : isOver ? 'Over budget by' : 'Left to spend'
  const leadValue = formatWhole(left == null ? counted : Math.abs(left))
  const detail = [
    limit != null ? `${formatWhole(counted)} spent of ${formatWhole(limit)}` : null,
    progress.isCurrent && limit != null
      ? `${progress.daysLeft} ${progress.daysLeft === 1 ? 'day' : 'days'} to go`
      : null,
    daily != null ? `about ${formatWhole(daily)} a day` : null,
  ].filter(Boolean)

  const side: { label: string; value: string; note?: string; tone: 'out' | 'in' }[] = []
  if (yearlySpent > 0 || data.yearly) {
    side.push({ label: 'Spent on yearly budgets', value: formatWhole(yearlySpent), tone: 'out' })
  }
  if (savings.savingsGoal != null) {
    side.push({
      label: 'Saved',
      value: formatWhole(savings.actualSavings),
      note: `of ${formatWhole(savings.savingsGoal)} goal`,
      tone: 'in',
    })
  }

  const leadFigure = (
    <p
      data-testid="hero-lead"
      className={cn(
        'shrink-0 font-serif text-[22px] font-bold leading-tight tabular-nums tracking-tight md:text-[40px] md:font-semibold',
        isOver && 'text-[var(--fl-hero-expense)]'
      )}
    >
      {leadValue}
    </p>
  )

  return (
    <section
      aria-label="Budget summary"
      className="flex flex-col gap-2 rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] px-3.5 py-3 text-[var(--fl-hero-fg)] md:gap-3 md:rounded-[20px] md:p-6"
    >
      {wide ? (
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)]">
          {periodLabel}
          {limit != null && ' · Monthly budget'}
        </p>
      ) : (
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)]">
            {leadLabel}
          </p>
          {leadFigure}
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          {wide && (
            <>
              <p className="text-xs font-medium text-[var(--fl-hero-fg-2)]">{leadLabel}</p>
              {leadFigure}
            </>
          )}
          {detail.length > 0 && (
            <p className="text-xs tabular-nums text-[var(--fl-hero-fg-2)] md:mt-0.5 md:text-[13px]">
              {detail.join(' · ')}
            </p>
          )}
        </div>
        {side.length > 0 && (
          <dl className="flex flex-wrap gap-x-4 gap-y-1 md:gap-6">
            {side.map((s) => (
              <div
                key={s.label}
                className="flex items-baseline gap-1.5 text-xs md:block md:text-[12.5px]"
              >
                <dt className="text-[var(--fl-hero-fg-2)]">{s.label}</dt>
                <dd
                  className={cn(
                    'font-semibold tabular-nums md:font-serif md:text-lg',
                    s.tone === 'out'
                      ? 'text-[var(--fl-hero-expense)]'
                      : 'text-[var(--fl-hero-income)]'
                  )}
                >
                  {s.value}
                  {s.note && (
                    <span className="ml-1 font-sans text-xs font-normal text-[var(--fl-hero-fg-2)]">
                      {s.note}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {limit != null && limit > 0 && (
        <div className="relative mt-1 h-2 rounded-full bg-[var(--fl-hero-bar-faint)] md:h-2.5">
          <div
            className={cn(
              'h-full rounded-full',
              isOver ? 'bg-[var(--fl-hero-expense)]' : 'bg-[var(--fl-hero-bar)]'
            )}
            style={{ width: `${Math.min(counted / limit, 1) * 100}%` }}
          />
          {progress.isCurrent && <TodayTick fraction={progress.elapsedFraction} />}
        </div>
      )}
    </section>
  )
}

/** Where today falls in the period, on a budget bar. */
export function TodayTick({ fraction }: { fraction: number }) {
  return (
    <span
      data-testid="today-tick"
      aria-hidden="true"
      className="absolute -top-1 h-[calc(100%+8px)] w-0.5 rounded-full bg-[var(--fl-hero-fg)]"
      style={{ left: `${fraction * 100}%` }}
    />
  )
}
